<script setup lang="ts">
/**
 * This component provides a standardised header that is used across the application.
 */

import { ENVIRONMENT } from "@/helpers/config";
import { routes } from "@/router/routes";
import { useAuthStore } from "@/stores/auth";
import { useUiStore } from "@/stores/ui";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import settings from "../../../package.json";
import { useTerminalsStore } from "@/stores/terminals";
import TerminalSelect from "../menu/TerminalSelect.vue";
import AssumeRoleMenu from "./AssumeRoleMenu.vue";
import LoadingCircle from "../shared/LoadingCircle.vue";
import { useRoute } from "vue-router";

const authStore = useAuthStore();
const uiStore = useUiStore();
const route = useRoute();
const terminalsStore = useTerminalsStore();

const {
  drawer,
  isLoading,
  terminalOperationId: operationId,
} = storeToRefs(uiStore);
const { activeTerminal } = storeToRefs(terminalsStore);

const frontendVersion = ref(settings.version);

const logOut = authStore.logOut;
const statusBarColor =
  ENVIRONMENT == "Dev" || ENVIRONMENT == "Test" || ENVIRONMENT == "UAT"
    ? "red"
    : "#1e196a";
const environmentText = ENVIRONMENT == "Prod" ? "" : ENVIRONMENT;

const mainLinks = routes
  // filter out routes that do not have meta.main is set
  .filter((route) => route.meta && route.meta.main)
  .map((route) => ({
    text: route.meta && route.meta.display,
    name: route.name,
    path: route.path,
  }));

const zlogoUrl = new URL("@/assets/Z-logo.svg", import.meta.url).href;

function resetNavigationValues() {
  terminalsStore.resetActiveTerminal();
  operationId.value = null;
}
</script>

<template>
  <v-app-bar
    app
    class="toolbar no-print"
    :color="statusBarColor"
    fixed
    dark
    clipped-left
  >
    <template v-for="item in mainLinks" :key="item.name">
      <router-link :to="{ name: item.name }" @click="resetNavigationValues">
        <img class="logo" width="50" :src="zlogoUrl" alt="Z" />
      </router-link>
    </template>

    <v-layout v-if="activeTerminal" mt-7>
      <div class="d-flex pt-10">
        <template v-if="!(operationId === null)">
          <v-tooltip v-if="drawer" bottom>
            <template #activator="{ props }">
              <v-btn
                icon
                color="white"
                dark
                left
                size="large"
                v-bind="props"
                @click.stop="drawer = !drawer"
              >
                <v-icon id="fullscreen" color="white" size="x-large">
                  fullscreen_exit
                </v-icon>
              </v-btn>
            </template>
            <span>Collapse Menu</span>
          </v-tooltip>
          <v-tooltip v-if="!drawer" bottom>
            <template #activator="{ props }">
              <v-btn
                icon
                color="white"
                dark
                left
                size="large"
                v-bind="props"
                @click.stop="drawer = !drawer"
              >
                <v-icon id="fullscreen" color="white" size="x-large">
                  fullscreen
                </v-icon>
              </v-btn>
            </template>
            <span>Expand Menu</span>
          </v-tooltip>
        </template>
      </div>
      <suspense>
        <TerminalSelect />
      </suspense>
    </v-layout>
    <v-layout v-else>
      <span class="menuHeader">{{ route.meta.title }}</span>
    </v-layout>
    <v-spacer />
    <div class="environmentText">
      {{ environmentText }}
    </div>
    <div class="loading-circle">
      <LoadingCircle v-show="isLoading" style="margin-right: 25px" />
    </div>
    <v-btn
      class="link"
      name="ApplicationMenuLinkSignOut"
      @click="() => logOut()"
    >
      Sign Out
    </v-btn>
    <AssumeRoleMenu />
    <span id="frontendVersion" class="label">v{{ frontendVersion }}</span>
  </v-app-bar>
</template>

<style lang="scss" scoped>
@use "../../styles/_responsive.scss";
@use "../../styles/_variables.scss";

#fullscreen {
  font-size: 36px;
}

.label {
  padding: 10px;
  margin-left: 8px;
  font-weight: bold;
  text-align: center;
  text-decoration: none;
  color: rgb(252, 252, 252) !important;
  font-size: 14px;
}

.menuHeader {
  text-align: center;
  color: #fff !important;
  font-size: 20px;
  margin: 0 8px;
  line-height: 24px;
  vertical-align: text-bottom;
  font-weight: bold;
}

.environmentText {
  font-weight: bold;
  color: #fff;
  font-size: 32px;
  padding-right: 150px;
  text-transform: uppercase;
}

:deep(.v-list) {
  padding-top: 1rem;
}

.link {
  font-weight: bold;
  text-align: center;
  text-decoration: none;
  color: #fff !important;
  font-size: 16px;
}

.logo {
  width: 50px;
  vertical-align: middle;
  margin-right: 18px;
  padding-left: 10px;
}

.sub-menu-offset {
  max-width: 100% !important;
}

.toolbar {
  z-index: 6;
}

.tank-sub-menu {
  background-color: variables.$mid-charcoal;
}

.loading-circle {
  display: block;
  min-height: 32px;
  min-width: 50px;
  width: 50px;
  height: 32px;
}

@media print {
  :deep(.v-content) {
    padding-top: 0 !important;
  }

  :deep(.no-print) {
    display: none !important;
  }
}
</style>
